import { useState } from 'react';
import { Modal } from '../../components/ui/Modal';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { AbsenceDialog } from './AbsenceDialog';
import { OvertimeDialog } from './OvertimeDialog';
import { useToast } from '../../components/ui/Toast';
import { useAbsences, useOvertime, absenceMutations, useDeleteOvertime } from '../../lib/queries';
import { formatDate, formatDayCount, formatHours } from '../../lib/dates';
import { ABSENCE_LABELS, ABSENCE_STATUS_LABELS, ABSENCE_COLOR } from '../../lib/labels';
import type { Absence, Balance, Employee } from '../../lib/types';

interface Props {
  employee: Employee;
  balance: Balance | undefined;
  employees: Employee[];
  canEdit: boolean;
  onClose: () => void;
}

/** Alles zu einem Mitarbeiter: Konten, Abwesenheiten, Überstundenbuchungen. */
export function EmployeeAvailability({ employee, balance, employees, canEdit, onClose }: Props) {
  const toast = useToast();
  const absences = useAbsences(employee.id);
  const overtime = useOvertime(employee.id);
  const removeAbsence = absenceMutations.useDelete();
  const removeOvertime = useDeleteOvertime();

  const [editingAbsence, setEditingAbsence] = useState<Absence | null | undefined>(undefined);
  const [addingOvertime, setAddingOvertime] = useState(false);
  const [deletingAbsence, setDeletingAbsence] = useState<Absence | null>(null);

  return (
    <>
      <Modal
        title={employee.displayName}
        subtitle={[employee.role, `${employee.weeklyHours} Std./Woche`].filter(Boolean).join(' · ')}
        onClose={onClose}
        width="breit"
        footer={
          <>
            {canEdit ? (
              <>
                <button type="button" className="btn" onClick={() => setAddingOvertime(true)}>
                  Überstunden buchen
                </button>
                <button type="button" className="btn btn-signal" onClick={() => setEditingAbsence(null)}>
                  Abwesenheit eintragen
                </button>
              </>
            ) : null}
            <div className="spacer" />
            <button type="button" className="btn" onClick={onClose}>
              Schließen
            </button>
          </>
        }
      >
        <div className="stack">
          {balance ? (
            <div className="stat-row">
              <Stat label="Urlaubsanspruch" value={formatDayCount(balance.vacationEntitlement)} />
              <Stat label="Genommen" value={formatDayCount(balance.vacationTaken)} />
              <Stat label="Beantragt" value={formatDayCount(balance.vacationRequested)} />
              <Stat
                label="Resturlaub"
                value={formatDayCount(balance.vacationRemaining)}
                tone={balance.vacationRemaining < 0 ? 'danger' : 'signal'}
              />
              <Stat label="Krank" value={formatDayCount(balance.sickDays)} />
              <Stat
                label="Überstunden"
                value={formatHours(balance.overtimeBalance)}
                tone={balance.overtimeBalance < 0 ? 'danger' : 'normal'}
              />
            </div>
          ) : null}

          <section>
            <span className="label">Abwesenheiten</span>
            {absences.isLoading ? (
              <p className="hint">Wird geladen …</p>
            ) : (absences.data ?? []).length === 0 ? (
              <p className="hint">Keine Einträge vorhanden.</p>
            ) : (
              <div className="table-scroll">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Art</th>
                      <th>Zeitraum</th>
                      <th>Status</th>
                      <th>Notiz</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {(absences.data ?? []).map((absence) => (
                      <tr key={absence.id}>
                        <td>
                          <span className="row" style={{ gap: 'var(--space-2)' }}>
                            <span
                              className="swatch"
                              style={{ background: ABSENCE_COLOR[absence.type] }}
                            />
                            {ABSENCE_LABELS[absence.type]}
                            {absence.halfDay ? <span className="badge">½ Tag</span> : null}
                          </span>
                        </td>
                        <td className="numeric nowrap">
                          {absence.startDate === absence.endDate
                            ? formatDate(absence.startDate)
                            : `${formatDate(absence.startDate)} – ${formatDate(absence.endDate)}`}
                        </td>
                        <td>
                          <span className={`badge status-${absence.status}`}>
                            {ABSENCE_STATUS_LABELS[absence.status]}
                          </span>
                        </td>
                        <td className="muted">{absence.note || '—'}</td>
                        <td className="align-right">
                          {canEdit ? (
                            <div className="row-actions">
                              <button
                                type="button"
                                className="btn btn-small"
                                onClick={() => setEditingAbsence(absence)}
                              >
                                Bearbeiten
                              </button>
                              <button
                                type="button"
                                className="btn btn-small btn-danger"
                                onClick={() => setDeletingAbsence(absence)}
                              >
                                Löschen
                              </button>
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section>
            <span className="label">Überstundenbuchungen</span>
            {(overtime.data ?? []).length === 0 ? (
              <p className="hint">
                Keine Buchungen. Startsaldo: {formatHours(employee.overtimeStartBalance)}
              </p>
            ) : (
              <div className="table-scroll">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Datum</th>
                      <th className="align-right">Stunden</th>
                      <th>Grund</th>
                      <th>Gebucht von</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {(overtime.data ?? []).map((entry) => (
                      <tr key={entry.id}>
                        <td className="numeric nowrap">{formatDate(entry.date)}</td>
                        <td
                          className="align-right numeric"
                          style={{
                            color: entry.hours < 0 ? 'var(--danger)' : 'var(--ok)',
                            fontWeight: 600,
                          }}
                        >
                          {formatHours(entry.hours)}
                        </td>
                        <td className="muted">{entry.reason || '—'}</td>
                        <td className="faint">{entry.createdBy || '—'}</td>
                        <td className="align-right">
                          {canEdit ? (
                            <div className="row-actions">
                              <button
                                type="button"
                                className="btn btn-small btn-danger"
                                onClick={() =>
                                  removeOvertime.mutate(entry.id, {
                                    onSuccess: () => toast.success('Buchung gelöscht.'),
                                    onError: () => toast.error('Löschen fehlgeschlagen.'),
                                  })
                                }
                              >
                                Löschen
                              </button>
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </Modal>

      {editingAbsence !== undefined ? (
        <AbsenceDialog
          absence={editingAbsence}
          employees={employees}
          defaultEmployeeId={employee.id}
          onClose={() => setEditingAbsence(undefined)}
        />
      ) : null}

      {addingOvertime ? (
        <OvertimeDialog
          employees={employees}
          defaultEmployeeId={employee.id}
          onClose={() => setAddingOvertime(false)}
        />
      ) : null}

      {deletingAbsence ? (
        <ConfirmDialog
          title="Abwesenheit löschen"
          message={`${ABSENCE_LABELS[deletingAbsence.type]} vom ${formatDate(deletingAbsence.startDate)} wirklich löschen?`}
          detail="Das Urlaubskonto wird entsprechend neu berechnet."
          confirmLabel="Löschen"
          busy={removeAbsence.isPending}
          onCancel={() => setDeletingAbsence(null)}
          onConfirm={() =>
            removeAbsence.mutate(
              { id: deletingAbsence.id },
              {
                onSuccess: () => {
                  toast.success('Eintrag gelöscht.');
                  setDeletingAbsence(null);
                },
                onError: () => {
                  toast.error('Löschen fehlgeschlagen.');
                  setDeletingAbsence(null);
                },
              },
            )
          }
        />
      ) : null}
    </>
  );
}

function Stat({
  label,
  value,
  tone = 'normal',
}: {
  label: string;
  value: string;
  tone?: 'normal' | 'signal' | 'danger';
}) {
  return (
    <div className={`stat stat-${tone}`}>
      <span className="label">{label}</span>
      <strong className="numeric">{value}</strong>
    </div>
  );
}
